import { useEffect, useState } from "react";
import ISale from "@/models/sales/isale";
import * as service from "@/services/sales-service";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import Spinner from "@/components/spinner";
import FormatCurrency from "@/common/format-currency";
import IStats from "@/models/sales/istats";
import ToggleSwitch from "@/components/toggle-switch/toggle-switch";
import ChangeYear from "./change-year";
import { useModal } from "@/components/modals/templating/modal";
import ViewSalesTable from "./view-table";
import { getRole } from "@/common/user-role";

const ViewSales: React.FC = () => {

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [ sales, setSales ] = useState<ISale[]>([]);
    const [ stats, setStats ] = useState<IStats>({} as IStats);
    const [ isGbp, setIsGbp ] = useState<boolean>(true);
    const [ year, setYear ] = useState<number>(0);

    const ChangeYearModal = useModal(<ChangeYear closeModal={(selectedYear: number) => closeChangeYearModal(selectedYear)} currentYear={year}/>);

    const navigate = useNavigate();

    if (getRole() !== "admin") {
        toast.error("You're not authorised to view that page");
        navigate("/");
    }

    async function updateData(selectedYear?: number){
        try {
            let response = await service.GetSales(selectedYear);

            if (response.length > 0) {
                setYear(new Date(response[0].startDate).getFullYear());
            } else {
                setYear(selectedYear ?? 0);
            }

            response = response.map(sale => {
                sale.cost = convertToGBP(sale.cost);
                sale.costPerPerson = convertToGBP(sale.costPerPerson);

                return sale;
            });
            
            setSales(response);

            updateStats(response);

            setIsLoading(false);
        } catch (e) {
            toast.error((e as Error).message);

            if (e instanceof Error && (e as Error).message.includes("Unauthorised")) {
                navigate("/login");
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => await updateData();

        fetchData();
    }, [])

    function setFormattedSales() {
        let data;

        if (!isGbp) {
            data = sales.map(sale => {
                sale.paidIncome = convertToGBP(sale.paidIncome);
                sale.totalIncome = convertToGBP(sale.totalIncome);
                sale.cost = convertToGBP(sale.cost);
                sale.costPerPerson = convertToGBP(sale.costPerPerson);

                return sale;
            });
        } else {
            data = sales.map(sale => {
                sale.paidIncome = convertToEur(sale.paidIncome);
                sale.totalIncome = convertToEur(sale.totalIncome);
                sale.cost = convertToEur(sale.cost);
                sale.costPerPerson = convertToEur(sale.costPerPerson);

                return sale;
            });
        }

        setSales(data);

        updateStats(data);
    }

    function convertToGBP(value: number) {
        return value * 0.884955752;
    }

    function convertToEur(value: number) {
        return value * 1.13
    }

    function updateStats(data: ISale[]){
        const income = data.reduce((total, current) => { return total + current.totalIncome }, 0);
        const cost = data.reduce((total, current) => { return total + current.cost }, 0);
        const profit = income-cost;
        const occupancy = data.reduce((total, current) => { return total + current.occupancy }, 0);
        const maxOccupancy = data.reduce((total, current) => { return total + current.maxOccupancy }, 0);
        const avgCpp = income/occupancy;
        const margin = Math.round((((income-cost)/cost)*100));

        setStats({
            income: income,
            profit: profit,
            totalOccupancy: occupancy,
            totalMaxOccupancy: maxOccupancy,
            averageCpp: avgCpp,
            margin: margin
        });
    }

    function updateCurrency() {

        setFormattedSales();

        setIsGbp(!isGbp)
    }

    function adjustYearBy(amount: number) {
        updateData(year + amount);
    }

    function closeChangeYearModal(selectedYear: number) {
        if (selectedYear != year) {
            updateData(selectedYear);
        }

        ChangeYearModal.closeModal();
    }

    return (
        <Spinner isLoading={isLoading}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-thin text-center">Sales overview</h1>
            <hr className="lg:w-2/3 mx-auto my-3" />
            
            <div className="flex justify-center flex-wrap text-xl mb-3">
                <div>Income: {FormatCurrency(stats.income, isGbp ? "£" : "€")} <span className="px-4">|</span> </div>
                <div>Profit: {FormatCurrency(stats.profit, isGbp ? "£" : "€")} <span className="px-4">|</span> </div>
                <div>{stats.totalOccupancy} / {stats.totalMaxOccupancy} <span className="px-4">|</span> </div>
                <div>Average CPP: {FormatCurrency(stats.averageCpp, isGbp ? "£" : "€")} <span className="px-4">|</span> </div>
                <div>Margin: {`${stats.margin}%`} <span className="px-4">|</span> </div>
                <div><span className="cursor-pointer" onClick={ChangeYearModal.openModal}>{`Year: ${year}`}</span> <span className="fas fa-arrow-circle-left text-sm pl-2 pr-1 cursor-pointer" style={{ lineHeight : 'inherit' }} onClick={() => adjustYearBy(-1)}></span><span className="fas fa-arrow-circle-right text-sm pl-1 cursor-pointer" style={{ lineHeight : 'inherit' }} onClick={() => adjustYearBy(1)}></span> <span className="px-4">|</span></div>
                <div className="pt-1"><ToggleSwitch isOn={isGbp} update={updateCurrency} /></div>
            </div>

            <ViewSalesTable sales={sales} currencySymbol={isGbp ? "£" : "€"} />

            {ChangeYearModal.modalOpen && <ChangeYearModal.ModalContent />}
        </Spinner>
    )
}

export default ViewSales;